import axiosInstance from "./axiosConfig";

/**
 * Service for shipping methods API
 */
const shippingService = {
  /**
   * Get available shipping methods for current order amount
   * @param {number} orderAmount - Current order amount
   * @returns {Promise<Array>} Available shipping methods
   */
  getAvailableShippingMethods: async (orderAmount) => {
    try {
      const response = await axiosInstance.get("/shipping/available", {
        params: { orderAmount },
      });
      return response.data;
    } catch (error) {
      console.error(
        "Shipping service error:",
        error.response?.data || error.message
      );

      // If it's a proper API error response with data, forward it
      if (error.response && error.response.data) {
        throw error.response.data;
      }

      // Otherwise create a generic error
      throw { message: "Error fetching available shipping methods" };
    }
  },

  /**
   * Get shipping method by ID
   * @param {string} id - Shipping method ID
   * @returns {Promise<Object>} Shipping method
   */
  getShippingMethodById: async (id) => {
    try {
      const response = await axiosInstance.get(`/shipping/${id}`);
      return response.data;
    } catch (error) {
      console.error(
        "Shipping service error:",
        error.response?.data || error.message
      );

      // If it's a proper API error response with data, forward it
      if (error.response && error.response.data) {
        throw error.response.data;
      }

      // Otherwise create a generic error
      throw { message: "Error fetching shipping method" };
    }
  },

  /**
   * Get all shipping methods (admin only)
   * @returns {Promise<Array>} All shipping methods
   */
  getAllShippingMethods: async () => {
    try {
      const response = await axiosInstance.get("/shipping");
      return response.data;
    } catch (error) {
      console.error(
        "Shipping service error:",
        error.response?.data || error.message
      );

      // If it's a proper API error response with data, forward it
      if (error.response && error.response.data) {
        throw error.response.data;
      }

      // Otherwise create a generic error
      throw { message: "Error fetching shipping methods" };
    }
  },

  /**
   * Create a new shipping method
   * @param {Object} shippingMethodData - Shipping method data
   * @returns {Promise<Object>} Created shipping method
   */
  createShippingMethod: async (shippingMethodData) => {
    const response = await axiosInstance.post("/shipping", shippingMethodData);
    return response.data;
  },

  /**
   * Update an existing shipping method
   * @param {string} id - Shipping method ID
   * @param {Object} shippingMethodData - Updated shipping method data
   * @returns {Promise<Object>} Updated shipping method
   */
  updateShippingMethod: async (id, shippingMethodData) => {
    const response = await axiosInstance.put(
      `/shipping/${id}`,
      shippingMethodData
    );
    return response.data;
  },

  /**
   * Delete a shipping method
   * @param {string} id - Shipping method ID
   * @returns {Promise<Object>} Delete confirmation
   */
  deleteShippingMethod: async (id) => {
    const response = await axiosInstance.delete(`/shipping/${id}`);
    return response.data;
  },
};

export default shippingService;
